import React, { Component } from 'react';
import PropTypes from 'prop-types';

import simplify from 'simplify-js';

import connectToStores from 'fluxible-addons-react/connectToStores';
import DesignCommentsStore from '../../stores/DesignCommentsStore';
import { enterDesignComment, resetSelectedComment } from '../../actions/design-comments';
import { addDesignPath } from '../../actions/design-sketches';

class InputHandler extends Component {
    constructor(props) {
        super(props);
        this.mouseDown = this.mouseDown.bind(this);
        this.mouseMove = this.mouseMove.bind(this);
        this.mouseUp = this.mouseUp.bind(this);
        this.touchStart = this.touchStart.bind(this);
        this.touchMove = this.touchMove.bind(this);
        this.touchEnd = this.touchEnd.bind(this);

        this.onClick = this.onClick.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);

        this.state = {
            currentPath: null,
            isHovering: false,
        };
    }

    componentDidMount() {
        // Mouse/Touch move and up events bound to window so that drawing can continiue outside the DOM element and browser window
        window.addEventListener('mousemove', this.mouseMove);
        window.addEventListener('mouseup', this.mouseUp);
        window.addEventListener('touchmove', this.touchMove);
        window.addEventListener('touchend', this.touchEnd);
    }

    componentWillUnmount() {
        // Mouse/Touch move and up events bound to window so that drawing can continiue outside the DOM element and browser window
        window.removeEventListener('mousemove', this.mouseMove);
        window.removeEventListener('mouseup', this.mouseUp);
        window.removeEventListener('touchmove', this.touchMove);
        window.removeEventListener('touchend', this.touchEnd);
    }

    onClick(event) {
        if (!this.props.canAnnotate) return;
        if (this.props.feedbackMode !== 'annotate' || this.props.isPresentationMode) return;

        const position = this.pointerPosition(event);

        if (this.props.isAnnotating) {
            this.context.executeAction(resetSelectedComment);
        } else {
            this.context.executeAction(enterDesignComment, {
                position,
                activeUser: this.context.activeUser,
            });
        }
    }

    onMouseEnter() {
        this.setState({
            isHovering: true,
        });
    }

    onMouseMove() {
        this.setState(state => (state.isHovering ? null : { isHovering: true }));
    }

    onMouseLeave() {
        this.setState({
            isHovering: false,
        });
    }

    onPointerDown(position) {
        this.setState(prevState => ({
            currentPath: [position],
        }));
    }

    onPointerMove(position) {
        this.setState(prevState => {
            if (prevState.currentPath !== null) {
                return {
                    currentPath: [...prevState.currentPath, position],
                };
            } else {
                return null;
            }
        });
    }

    onPointerUp(position) {
        if (this.state.currentPath) {
            if (this.state.currentPath.length >= 3) {
                // Simplify the path before saving. Tolerance of 2px and highQuality=true
                const path = simplify(this.state.currentPath, 2.0, true);
                this.props.addDesignPath(path);
            }
            // TODO dhotson: perhaps if the path is too short, trigger comment annotation?

            this.setState({ currentPath: null });
        }
    }

    mouseDown(event) {
        if (!this.props.canSketch) return;
        if (this.props.feedbackMode !== 'sketch' || this.props.isPresentationMode) return;

        // Trigger on primary mouse click only
        if (event.button == 0) {
            event.preventDefault();
            this.onPointerDown(this.pointerPosition(event));
        }
    }

    mouseUp(event) {
        if (!this.props.canSketch) return;
        if (this.props.feedbackMode !== 'sketch' || this.props.isPresentationMode) return;

        event.preventDefault();
        this.onPointerUp(this.pointerPosition(event));
    }

    mouseMove(event) {
        if (!this.props.canSketch) return;
        if (this.props.feedbackMode !== 'sketch' || this.props.isPresentationMode) return;

        event.preventDefault();
        this.onPointerMove(this.pointerPosition(event));
    }

    touchStart(event) {
        if (!this.props.canSketch) return;
        if (this.props.feedbackMode !== 'sketch' || this.props.isPresentationMode) return;

        event.preventDefault(); // preventDefault on touch start prevents scrolling
        const touch = event.touches.item(0);
        this.onPointerDown(this.pointerPosition(touch));
    }

    touchEnd(event) {
        if (!this.props.canSketch) return;
        if (this.props.feedbackMode !== 'sketch' || this.props.isPresentationMode) return;
        if (this.state.currentPath === null) return;

        const touch = event.changedTouches.item(0);
        this.onPointerUp(this.pointerPosition(touch));
    }

    touchMove(event) {
        if (!this.props.canSketch) return;
        if (this.props.feedbackMode !== 'sketch' || this.props.isPresentationMode) return;
        if (this.state.currentPath === null) return;

        const touch = event.touches.item(0);
        this.onPointerMove(this.pointerPosition(touch));
    }

    // Calculate position of artboard element relative to document
    // by iterating up through element offsetParent's
    elementPosition() {
        let el = this.el;
        let left = el.offsetLeft - el.scrollLeft;
        let top = el.offsetTop - el.scrollTop;
        while (el) {
            left += el.offsetLeft - el.scrollLeft;
            top += el.offsetTop - el.scrollTop;
            el = el.offsetParent;
        }

        return {
            left,
            top,
        };
    }

    // Calculate mouse position relative to artboard element
    pointerPosition(eventOrTouch) {
        const elementPosition = this.elementPosition();
        // Calculate mouse position relative to artboard element
        return this.props.toImageSpace({
            x: eventOrTouch.clientX - elementPosition.left,
            y: eventOrTouch.clientY - elementPosition.top,
        });
    }

    cursor() {
        if (this.props.isPresentationMode) {
            return 'default';
        }

        if (this.props.feedbackMode === 'annotate' && this.props.canAnnotate) {
            return 'crosshair';
        }

        if (this.props.feedbackMode === 'sketch' && this.props.canSketch) {
            return this.pencilCursor();
        }

        return 'default';
    }

    pencilCursor() {
        const cursor1x =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAAMFBMVEVHcEwAAAAKCgqcnJzZ2dn19fX////u7u4AAADm5uaUlJRVVVV2dnbv7+8pKSm7u7u4OrkAAAAAEHRSTlMABg8/cP7/rRyW////3jJXidydOwAAAJNJREFUeNrt0bsOxCAMRNEFg3k5y///bTwFipQRSbXdTnsuSIjPf7sF7IGjJIkx7D1l1VJXwd60D8ttFeTiPoaprIDOqw2fJgTsSbtlL/q6gd2vP6xrqjGwFziOa/siIM9wBLJxA+ONL15e/IAHej/5Lch97wimklNAToF7WU6BKLZ1/PKcU+rlXMTqfDkXwRX8g52QCAfFqDsYpAAAAABJRU5ErkJggg==';
        const cursor2x =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAMAAACdt4HsAAAAYFBMVEVHcEwAAAAAAAD4+PgAAAAAAAAKCgoAAAAAAAAAAAAAAAAAAAAAAADg4ODq6ur09PS+vr4/Pz+3t7fZ2dmRkZHa2tr6+vr///9gYGBVVVWWlpaGhobExMTg4OBzc3OqqqqL1npXAAAAF3RSTlMAEx35DBAYAQMHIy8okq/YcUBOcUaD4ygdGvAAAAHgSURBVHhe7ZbrjqMwDEYnCcQ0hZbexvT+/m+5tpNOtFKBmJVWGqlHqvrrfNiOEfn6RXz48AEAGgKIZXojpIxlT19FKEKdkPSKoX91AiS9ZihCEnQ+62bfbU5Hb2plArfPT/dbZDrLEZyg882efUe/TfCmkgSdT+5tOF/viB0lcBdQ7K8q0yPi80wMlNBbH5so971DdzkLD4ffwcYSyv0DIvvCBfEQfM0Bxf1/I+L1/IKnkHoo9JFxD20ANHn+T5r+ffhp4VQSENuvxb/w7CRBfLcrCACI65t8Sbgl/9gGL6cA035+vnDlTRJ/HayZLgBe9R/wr/MT9uTLKTYw5/vsCzfSXb9uZxcx+7x/mSdiv2PfxBd63s+Hn16D7frffKT6w8uHmfm/8zfRrwp8P+Zb8ucOgP3NmD/RvxB9M+7HA5wZ4Ft/u5MFkhWeKYDndx/G/Ol3CMivjg7dG78t8pvYwFVfvyBH6BFv+vpzB/UB8bHUlw46xAlfF5Dnl31FgNrPAeP9FwYMol/i/pb7eQ8TGl8ASG9iwnXkl/afF8n4vhP267YNuhtN+hh6G0JLhGA1fk6ojffWWu+jvirW8xehNsTPla4B/Y0ysuRaCjEi0ah9SciIrgbyxT7rWqL8H/kD+qxPB7g2J9wAAAAASUVORK5CYII=';
        return `-webkit-image-set(url(${cursor1x}) 1x, url(${cursor2x}) 2x) 7 27, pointer`;
    }

    render() {
        return (
            <div
                data-input-handler
                onClick={this.onClick}
                onTouchStart={this.touchStart}
                onMouseDown={this.mouseDown}
                onMouseMove={this.onMouseMove}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
                ref={el => (this.el = el)}
                style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    cursor: this.cursor(),
                }}
            >
                {this.props.children({
                    isEditing: this.state.currentPath || this.props.isAnnotating,
                    currentPath: this.state.currentPath,
                    isHovering: this.state.isHovering,
                })}
            </div>
        );
    }
}

InputHandler.contextTypes = {
    executeAction: PropTypes.func,
    activeUser: PropTypes.object,
};

InputHandler.propTypes = {
    canAnnotate: PropTypes.bool.isRequired,
    canSketch: PropTypes.bool.isRequired,
    isAnnotating: PropTypes.bool.isRequired,
    feedbackMode: PropTypes.string.isRequired,
    addDesignPath: PropTypes.func.isRequired,
    toImageSpace: PropTypes.func.isRequired,
    isPresentationMode: PropTypes.bool.isRequired,
    children: PropTypes.func,
};

export const ConnectedInputHandler = connectToStores(
    InputHandler,
    [DesignCommentsStore],
    (ctx, props) => ({
        isAnnotating: Boolean(ctx.getStore(DesignCommentsStore).selectedComment),
        addDesignPath: path =>
            ctx.executeAction(addDesignPath, {
                designId: props.designId,
                path,
                csrfToken: ctx.csrfToken,
            }),
    })
);

ConnectedInputHandler.contextTypes = {
    getStore: PropTypes.func,
    executeAction: PropTypes.func,
    csrfToken: PropTypes.string,
};

ConnectedInputHandler.propTypes = {
    feedbackMode: PropTypes.string.isRequired,
    toImageSpace: PropTypes.func.isRequired,
    designId: PropTypes.number.isRequired,
};



// WEBPACK FOOTER //
// ./~/@99designs/ddo/components/DesignPane/InputHandler.js